import { Content } from '@raydiant/api-client-js';
import { LibraryPageModalType } from '../../LibraryPageV2';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import { selectUserProfile } from '../../../../../selectors/user';
import { useSelector } from 'react-redux';
import { cloneElement, useCallback, useState } from 'react';
import Grid from '../../../../../components/Grid';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import { createNodeId } from '../../../../../utilities';
import GridItem from '../../../../../components/Grid/GridItem';
import GridItemDraggable from '../../../../../components/Grid/GridItemDraggable';
import LibraryContentCard from '../LibraryContentCard';

export interface GridViewLayoutProps {
  contents: Content[];
  showOwner?: boolean;
  folderOwnerLabel?: string;
  hideMoveOption?: boolean;
  libraryQuery?: typeof DEFAULT_QUERY;
  batchMode?: boolean;
  isTopLevelSharedLibraryFolder?: boolean;
  hideShare?: boolean;
  selectedNodeIds: string[];
  // resourceId of the "See in Library" target to highlight + scroll to ('' = none).
  highlightedResourceId?: string;
  folderId: string;
  isMoveable: boolean;
  onMove: (nodeIds: string[], folderId: string | null) => void;
  onToggleNodeId: (content: Content) => void;
  onSelectNodeIds: (nodeIds: string[]) => void;
  onClick: (content: Content) => void;
  onButtonGroupClick: (resourceId: string) => void;
  onButtonGroupItemClick: (modalType: LibraryPageModalType) => void;
  onClose: () => void;
  onCreateNewPlaylist?: (content: Content) => void;
  onAddToPlaylist?: (content: Content) => void;
  onDuplicateFolder?: (content: Content) => void;
  onDuplicateContent?: (content: Content) => void;
  showDuplicateAndMove?: boolean;
}

const GridViewLayout = ({
  contents,
  showOwner = false,
  folderOwnerLabel,
  hideMoveOption = false,
  libraryQuery = DEFAULT_QUERY,
  batchMode = false,
  isTopLevelSharedLibraryFolder = false,
  hideShare = false,
  selectedNodeIds,
  highlightedResourceId = '',
  folderId,
  isMoveable,
  onMove,
  onToggleNodeId,
  onClick,
  onSelectNodeIds,
  onButtonGroupClick,
  onButtonGroupItemClick,
  onClose,
  onCreateNewPlaylist,
  onAddToPlaylist,
  onDuplicateFolder,
  onDuplicateContent,
  showDuplicateAndMove = false,
}: GridViewLayoutProps) => {
  // Selectors
  const currentUser = useSelector(selectUserProfile);

  // State
  const [intersectedNodeIds, setIntersectedNodeIds] = useState<string[]>([]);

  // Callbacks

  const getDragStack = useCallback(
    (draggingNodeId: string) => {
      return [
        draggingNodeId,
        ...selectedNodeIds.filter((nodeId) => nodeId !== draggingNodeId),
      ];
    },
    [selectedNodeIds],
  );

  const onDrop = useCallback(
    (nodeIds: string[]) => {
      onMove(nodeIds, folderId || null);
    },
    [onMove, folderId],
  );

  const onCanDrop = useCallback(
    (nodeIds: string[]) => {
      if (!isMoveable) return false;
      return false;
    },
    [isMoveable],
  );

  const isNodeSelected = useCallback(
    (nodeId: string) => {
      return (
        selectedNodeIds.includes(nodeId) || intersectedNodeIds.includes(nodeId)
      );
    },
    [selectedNodeIds, intersectedNodeIds],
  );

  const isHighlighted = useCallback(
    (content: Content) =>
      !!highlightedResourceId && content.resourceId === highlightedResourceId,
    [highlightedResourceId],
  );

  if (!currentUser) return null;
  return (
    <Scrollable>
      {(containerRef) => (
        <Grid
          selectable
          containerRef={containerRef}
          selected={selectedNodeIds}
          getDragStack={getDragStack}
          onDrop={onDrop}
          onCanDrop={onCanDrop}
          onIntersect={setIntersectedNodeIds}
          onSelect={onSelectNodeIds}
        >
          {contents.map((content) => {
            if (content.resourceType === 'presentation') {
              const nodeId = createNodeId('presentation', content.id);

              return (
                <Scrollable.VisibilitySensor key={nodeId}>
                  {({ visibilityRef, isVisible }) => {
                    const presentationEl = (
                      <LibraryContentCard
                        content={content}
                        onClick={onClick}
                        onButtonGroupClick={onButtonGroupClick}
                        onButtonGroupItemClick={onButtonGroupItemClick}
                        onClose={onClose}
                        batchMode={batchMode}
                        selected={isNodeSelected(nodeId)}
                        highlighted={isHighlighted(content)}
                        onSelect={onToggleNodeId}
                        showOwner={
                          content.ownedBy?.id !== currentUser.id || showOwner
                        }
                        ownerLabel={folderOwnerLabel}
                        hideMoveOption={hideMoveOption}
                        libraryQuery={libraryQuery}
                        onCreateNewPlaylist={onCreateNewPlaylist}
                        onAddToPlaylist={onAddToPlaylist}
                        onDuplicateContent={onDuplicateContent}
                        hideShare={hideShare}
                        showDuplicateAndMove={showDuplicateAndMove}
                      />
                    );

                    if (!isMoveable) {
                      return (
                        <GridItem ref={visibilityRef} nodeId={nodeId}>
                          {presentationEl}
                        </GridItem>
                      );
                    }

                    return (
                      <GridItemDraggable ref={visibilityRef} nodeId={nodeId}>
                        {(dragRef) =>
                          cloneElement(presentationEl, { ref: dragRef })
                        }
                      </GridItemDraggable>
                    );
                  }}
                </Scrollable.VisibilitySensor>
              );
            }

            if (content.resourceType === 'playlist') {
              const nodeId = createNodeId('playlist', content.id);

              const playlistEl = (
                <LibraryContentCard
                  content={content}
                  onClick={onClick}
                  onButtonGroupClick={onButtonGroupClick}
                  onButtonGroupItemClick={onButtonGroupItemClick}
                  onClose={onClose}
                  batchMode={batchMode}
                  selected={isNodeSelected(nodeId)}
                  highlighted={isHighlighted(content)}
                  onSelect={onToggleNodeId}
                  showOwner={
                    content.ownedBy?.id !== currentUser.id || showOwner
                  }
                  ownerLabel={folderOwnerLabel}
                  hideMoveOption={hideMoveOption}
                  libraryQuery={libraryQuery}
                  onCreateNewPlaylist={onCreateNewPlaylist}
                  onAddToPlaylist={onAddToPlaylist}
                  onDuplicateContent={onDuplicateContent}
                  hideShare={hideShare}
                  showDuplicateAndMove={showDuplicateAndMove}
                />
              );

              if (!isMoveable) {
                return (
                  <GridItem key={nodeId} nodeId={nodeId}>
                    {playlistEl}
                  </GridItem>
                );
              }

              return (
                <GridItemDraggable key={nodeId} nodeId={nodeId}>
                  {(dragRef) => cloneElement(playlistEl, { ref: dragRef })}
                </GridItemDraggable>
              );
            }

            if (content.resourceType === 'folder') {
              const nodeId = createNodeId('folder', content.id);

              const folderEl = (
                <LibraryContentCard
                  content={content}
                  onClick={onClick}
                  onButtonGroupClick={onButtonGroupClick}
                  onButtonGroupItemClick={onButtonGroupItemClick}
                  onClose={onClose}
                  batchMode={batchMode}
                  selected={isNodeSelected(nodeId)}
                  highlighted={isHighlighted(content)}
                  onSelect={onToggleNodeId}
                  showOwner={
                    content.ownedBy?.id !== currentUser.id || showOwner
                  }
                  ownerLabel={folderOwnerLabel}
                  hideMoveOption={hideMoveOption}
                  libraryQuery={libraryQuery}
                  isTopLevelSharedLibraryFolder={isTopLevelSharedLibraryFolder}
                  hideShare={hideShare}
                  onDuplicateFolder={onDuplicateFolder}
                  showDuplicateAndMove={showDuplicateAndMove}
                />
              );

              if (!isMoveable) {
                return (
                  <GridItem key={nodeId} nodeId={nodeId}>
                    {folderEl}
                  </GridItem>
                );
              }

              return (
                <GridItemDraggable
                  key={nodeId}
                  nodeId={nodeId}
                  onDrop={(nodeIds) => onMove(nodeIds, content.id)}
                  onCanDrop={(nodeIds) =>
                    !nodeIds.some((nId) => nId === nodeId)
                  }
                >
                  {(dragRef) => cloneElement(folderEl, { ref: dragRef })}
                </GridItemDraggable>
              );
            }

            return null;
          })}
        </Grid>
      )}
    </Scrollable>
  );
};

export default GridViewLayout;
